<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';
import type { PropType } from 'vue';

import { DirectSaleComputedState, SellingMethod } from '~/apiClient';
import { type SaleItemEntity, isAuction, isDirectSale } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tn, tt } = useTypedI18n();
const { title, titleColor, titleAnnotation, titleAnnotationColor, priceColor, isSeller, isFeeNoticeHidden } = isAuction(sale)
    ? useAuctionStatus(toRef(() => sale))
    : useDirectSaleStatus(toRef(() => sale));
const price = computed(() => (sale?.sellingMethod === SellingMethod.Auction ? sale.currentPrice : sale.price));
</script>

<template>
    <div class="flex flex-col gap-3 lg:gap-4">
        <p class="flex gap-3 text-xs font-extrabold tracking-widest uppercase lg:text-sm lg:max-xl:flex-col">
            <span :class="titleColor">{{ title }}</span>
            <span v-if="titleAnnotation" :class="titleAnnotationColor">{{ titleAnnotation }}</span>
        </p>

        <template v-if="lodashIsNumber(price)">
            <p class="text-2xl font-semibold lg:text-5xl" :class="priceColor">{{ tn(price, 'currency') }}</p>
            <SaleFees
                v-if="!isFeeNoticeHidden"
                :reference-date="sale.sellingMethod === SellingMethod.Auction ? sale.startAt : undefined"
                :is-buyer="!isSeller"
                :base-amount="price"
                :assets-count="sale.assets.length"
                :fee-configuration="sale.userMeta?.feeOverride"
                wrapper-class="text-left"
            />
            <p
                v-if="isDirectSale(sale) && sale.computedState === DirectSaleComputedState.NoActiveOrder && sale.buyNowPrice"
                class="text-dimmed text-xs sm:text-sm"
            >
                {{ tt('sdp.DirectSale.directSaleStatus.buyNowPrice') }}:
                <span class="text-highlighted font-semibold">{{ tn(sale.buyNowPrice, 'currency') }}</span>
            </p>
        </template>
        <p v-else class="text-2xl font-semibold uppercase lg:text-5xl" :class="priceColor">*******</p>

        <template v-if="isAuction(sale)">
            <AdpCountdown class="text-sm lg:text-base" :auction="sale" />
            <AuctionReservePriceNotice :auction="sale" class="self-start" />
        </template>
    </div>
</template>
